import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  AccountSearch,
  AccountSearchSort,
} from 'crono-fe-common/types/DTO/accountSearch';
import { ProspectSearch } from 'crono-fe-common/types/DTO/prospectSearch';
import { useAuth } from 'context/auth';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { Constants } from 'crono-fe-common/constants/constants';
import { filterNullProperties } from 'utils/uiHelpers';
import { isCurrentUserSelection } from 'hooks/useUsersFilter';
import type { DashboardTabType } from '../../lists/dashboardTabType';

interface FiltersContextType {
  defaultFilters: AccountSearch | ProspectSearch;
  searchAccountParameters: AccountSearch;
  setSearchAccountParameters: React.Dispatch<
    React.SetStateAction<AccountSearch>
  >;
  resetAccountFilters: (saveInPreferences?: boolean) => void;
  areThereAccountFiltersApplied: boolean;
  resetAccountFiltersAndOwnedFalse: (saveInPreferences?: boolean) => void;
  searchProspectParameters: ProspectSearch;
  setSearchProspectParameters: React.Dispatch<
    React.SetStateAction<ProspectSearch>
  >;
  searchLeadParameters: ProspectSearch;
  setSearchLeadParameters: React.Dispatch<React.SetStateAction<ProspectSearch>>;
  resetProspectFilters: (
    saveInPreferences?: boolean,
    tabState?: DashboardTabType,
  ) => void;
  resetProspectFiltersAndOwnedFalse: (
    saveInPreferences?: boolean,
    tabState?: DashboardTabType,
  ) => void;
  areThereProspectFiltersApplied: boolean;
  areThereLeadFiltersApplied: boolean;
  updateUserPreferences: (
    tabState: DashboardTabType,
    newFilters: ProspectSearch | AccountSearch,
    saveInPreferences?: boolean,
  ) => void;
}

export const FiltersContext = createContext<FiltersContextType | undefined>(
  undefined,
);

FiltersContext.displayName = 'FiltersContext';

// RelevanceFirst only earns its keep once there's a name term to rank
// against - the API itself degrades a term-less RelevanceFirst to its own
// LastActivity-desc default, which every call site here wants to avoid.
// Every consumer of searchAccountParameters that fires a request should run
// it through here rather than sending `sort` as-is.
export const getEffectiveAccountSort = (
  params: Pick<AccountSearch, 'sort' | 'name'>,
): AccountSearchSort =>
  params.sort === 'RelevanceFirst' && !params.name?.trim()
    ? 'Name'
    : params.sort;

// All owner-related keys (legacy single-owner + multi user/team + "no owner").
// Owner selection is evaluated separately (see areNoSameOwnerFiltersApplied), so
// these keys are excluded from the generic "applied filters" comparison.
const OWNER_FILTER_KEYS = ['userId', 'userIds', 'userListIds', 'noUser'];

// Keys ignored when comparing a tab's filters against the defaults to decide
// whether any "real" filter is applied.
const NON_COMPARABLE_FILTER_KEYS = [
  ...OWNER_FILTER_KEYS,
  'name',
  'sort',
  'selected',
  'fromContact',
];

const omitFilterKeys = <T extends object>(filters: T): Partial<T> => {
  const rest: Partial<T> = { ...filters };
  NON_COMPARABLE_FILTER_KEYS.forEach((key) => {
    delete rest[key as keyof T];
  });
  return rest;
};

// True when an owner filter other than the "my records" selection is set.
const areNoSameOwnerFiltersApplied = (
  params: {
    userIds?: number[] | null;
    userListIds?: number[] | null;
    noUser?: boolean | null;
  },
  currentUserId?: number,
): boolean => {
  const userIds = params.userIds ?? [];
  const userListIds = params.userListIds ?? [];

  return (
    !isCurrentUserSelection(userIds, userListIds, currentUserId) &&
    (!!params.noUser || userIds.length > 0 || userListIds.length > 0)
  );
};

// True when any non-owner and non-omitted filter differs from the defaults.
const hasActualNonOwnerFiltersApplied = (
  params: object,
  defaults: object,
): boolean =>
  JSON.stringify(filterNullProperties(omitFilterKeys(params))) !==
  JSON.stringify(filterNullProperties(omitFilterKeys(defaults)));

const FiltersProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const PROSPECT_DEFAULT_FILTERS = useMemo(
    () => ({
      limit: Constants.defaultLimit,
      sort: 'Name',
      status: null,
      name: null,
      offset: 0,
      userIds: user?.id ? [user.id] : null,
      userListIds: null,
    }),
    [user?.id],
  );

  const [searchAccountParameters, setSearchAccountParameters] =
    useState<AccountSearch>({
      ...PROSPECT_DEFAULT_FILTERS,
      // Unlike Prospects/Leads, Account search supports relevance-based
      // ordering: RelevanceFirst goes out on every request, and the API
      // applies it only when a name term is present, otherwise falling back
      // to its own default (last-activity) ordering.
      sort: 'RelevanceFirst',
    } as AccountSearch);

  const resetAccountFilters = (saveInPreferences = false) => {
    const newSearchParameters = {
      ...PROSPECT_DEFAULT_FILTERS,
      sort: searchAccountParameters.sort,
      name: searchAccountParameters.name,
      selected: searchAccountParameters.selected,
    };
    setSearchAccountParameters(newSearchParameters);
    if (saveInPreferences) {
      updateUserPreferences('Company', newSearchParameters);
    }
  };

  const resetAccountFiltersAndOwnedFalse = (saveInPreferences = false) => {
    const newSearchParameters = {
      ...PROSPECT_DEFAULT_FILTERS,
      sort: searchAccountParameters.sort,
      name: searchAccountParameters.name,
      selected: searchAccountParameters.selected,
      userIds: null,
      userListIds: null,
    };
    setSearchAccountParameters(newSearchParameters);
    if (saveInPreferences) {
      updateUserPreferences('Company', newSearchParameters);
    }
  };

  //This is used to show different empty states in the table
  const areThereAccountFiltersApplied = useMemo(() => {
    if (areNoSameOwnerFiltersApplied(searchAccountParameters, user?.id)) {
      return true;
    }
    return hasActualNonOwnerFiltersApplied(
      searchAccountParameters,
      PROSPECT_DEFAULT_FILTERS,
    );
  }, [searchAccountParameters, user?.id, PROSPECT_DEFAULT_FILTERS]);

  const [searchProspectParameters, setSearchProspectParameters] =
    useState<ProspectSearch>({
      ...PROSPECT_DEFAULT_FILTERS,
      fromContact: true,
    } as ProspectSearch);
  const [searchLeadParameters, setSearchLeadParameters] =
    useState<ProspectSearch>({
      ...PROSPECT_DEFAULT_FILTERS,
      fromContact: false,
    } as ProspectSearch);

  const resetProspectFilters = (
    saveInPreferences = false,
    tabState: DashboardTabType = 'Contact',
  ) => {
    let newSearchParameters: ProspectSearch | null = null;

    if (tabState === 'Contact') {
      newSearchParameters = {
        ...PROSPECT_DEFAULT_FILTERS,
        sort: searchProspectParameters.sort,
        name: searchProspectParameters.name,
        selected: searchProspectParameters.selected,
        fromContact: true,
      };
      setSearchProspectParameters(newSearchParameters);
    } else if (tabState === 'Lead') {
      newSearchParameters = {
        ...PROSPECT_DEFAULT_FILTERS,
        sort: searchLeadParameters.sort,
        name: searchLeadParameters.name,
        selected: searchLeadParameters.selected,
        fromContact: false,
      };
      setSearchLeadParameters(newSearchParameters);
    }

    if (saveInPreferences && newSearchParameters) {
      updateUserPreferences(tabState, newSearchParameters);
    }
  };

  const resetProspectFiltersAndOwnedFalse = (
    saveInPreferences = false,
    tabState: DashboardTabType = 'Contact',
  ) => {
    let newSearchParameters: ProspectSearch | null = null;

    if (tabState === 'Contact') {
      newSearchParameters = {
        ...PROSPECT_DEFAULT_FILTERS,
        sort: searchProspectParameters.sort,
        name: searchProspectParameters.name,
        selected: searchProspectParameters.selected,
        userIds: null,
        userListIds: null,
        fromContact: true,
      };
      setSearchProspectParameters(newSearchParameters);
    } else if (tabState === 'Lead') {
      newSearchParameters = {
        ...PROSPECT_DEFAULT_FILTERS,
        sort: searchLeadParameters.sort,
        name: searchLeadParameters.name,
        selected: searchLeadParameters.selected,
        userIds: null,
        userListIds: null,
        fromContact: false,
      };
      setSearchLeadParameters(newSearchParameters);
    }

    if (saveInPreferences && newSearchParameters) {
      updateUserPreferences(tabState, newSearchParameters);
    }
  };

  //This is used to show different empty states in the table
  const areThereProspectFiltersApplied = useMemo(() => {
    if (areNoSameOwnerFiltersApplied(searchProspectParameters, user?.id)) {
      return true;
    }
    return hasActualNonOwnerFiltersApplied(
      searchProspectParameters,
      PROSPECT_DEFAULT_FILTERS,
    );
  }, [searchProspectParameters, user?.id, PROSPECT_DEFAULT_FILTERS]);

  const areThereLeadFiltersApplied = useMemo(() => {
    if (areNoSameOwnerFiltersApplied(searchLeadParameters, user?.id)) {
      return true;
    }
    return hasActualNonOwnerFiltersApplied(
      searchLeadParameters,
      PROSPECT_DEFAULT_FILTERS,
    );
  }, [searchLeadParameters, user?.id, PROSPECT_DEFAULT_FILTERS]);

  const { mutate: patchUserPreferences } = usePatchUserPreferences();

  const updateUserPreferences = (
    tabState: DashboardTabType,
    newFilters: ProspectSearch | AccountSearch,
    saveInPreferences = true,
  ) => {
    const filtersToSave = { ...newFilters, name: undefined };
    switch (tabState) {
      case 'Company': {
        const userPreferencesStringCompany = JSON.stringify(filtersToSave);
        if (user) {
          if (user.userPreferences) {
            user.userPreferences.accountTableFilters =
              userPreferencesStringCompany;
          } else {
            user.userPreferences = {
              accountTableFilters: userPreferencesStringCompany,
            };
          }
        }
        if (saveInPreferences) {
          patchUserPreferences({
            accountTableFilters: userPreferencesStringCompany,
          });
        }
        break;
      }
      case 'Contact': {
        const userPreferencesStringContact = JSON.stringify(filtersToSave);
        if (user) {
          if (user.userPreferences) {
            user.userPreferences.prospectTableFilters =
              userPreferencesStringContact;
          } else {
            user.userPreferences = {
              prospectTableFilters: userPreferencesStringContact,
            };
          }
        }
        if (saveInPreferences) {
          patchUserPreferences({
            prospectTableFilters: userPreferencesStringContact,
          });
        }
        break;
      }
      case 'Lead':
        {
          const userPreferencesStringLead = JSON.stringify(filtersToSave);
          if (user) {
            if (user.userPreferences) {
              user.userPreferences.leadTableFilters = userPreferencesStringLead;
            } else {
              user.userPreferences = {
                leadTableFilters: userPreferencesStringLead,
              };
            }
          }
          if (saveInPreferences) {
            patchUserPreferences({
              leadTableFilters: userPreferencesStringLead,
            });
          }
        }
        break;
    }
  };

  return (
    <FiltersContext.Provider
      value={{
        defaultFilters: PROSPECT_DEFAULT_FILTERS as AccountSearch,
        searchAccountParameters,
        setSearchAccountParameters,
        resetAccountFiltersAndOwnedFalse,
        resetAccountFilters,
        areThereAccountFiltersApplied,
        searchProspectParameters,
        setSearchProspectParameters,
        resetProspectFilters,
        resetProspectFiltersAndOwnedFalse,
        areThereProspectFiltersApplied,
        areThereLeadFiltersApplied,
        searchLeadParameters,
        setSearchLeadParameters,
        updateUserPreferences,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};

export default FiltersProvider;

export function useFilters() {
  const context = useContext(FiltersContext);
  if (context === undefined) {
    throw new Error(`useFilters must be used within a FiltersProvider`);
  }

  return context;
}
